'use client';

import type { CartOrderType } from '@order-up/models';
import { applyServerRepricing, Cart, CartItem, ICartItemData, type CartLineRefusal, type IOrderDeliveryAddressComponents, ITaxRate } from '@order-up/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { cartService } from '../services/cart';
import { CartStorageService } from '../services/cartStorage';
import {
    CartAlreadyConvertedError,
    CartNotOwnedError,
    cartsApi,
    type UpsertCartResult,
} from '../services/cartsApi';
import { useAuth } from './useAuth';
import { createLogger } from '../utils/logger';

const logger = createLogger('useCart');

/**
 * THE cart write: through the API (`PUT /carts/:guid`), never the client
 * Firestore SDK. Firebase App Check (reCAPTCHA Enterprise) refuses some real
 * iPhone Safari guests, and with App Check enforced the SDK write never acked —
 * checkout failed. The API authenticates the guest, allocates the cart id
 * server-side in the write's transaction, and does not depend on App Check.
 *
 * There is no SDK fallback: the temporary one (taken when the API answered a
 * bare 404, i.e. a storefront deployed ahead of the API) was removed once the
 * routes were live in production. A bare 404 is now an ordinary failure.
 */
async function upsertCartThroughApi(
    cart: Cart,
    options: { mustExist?: boolean } = {},
): Promise<UpsertCartResult> {
    return cartsApi.upsertCart(cart, options);
}

/**
 * Persist `current` for `customerId`; when the server refuses it for good — it
 * already became an order (legacy fallback path; the API re-homes those itself)
 * or its guid belongs to another uid (a guest cart adopted after signing in to
 * a different account) — move the items onto a FRESH local identity owned by
 * the caller and persist that. The server allocates the new cart's id.
 */
async function persistCartWithRehome(
    current: Cart,
    customerId: string | undefined,
    isAnonymous: boolean | undefined,
): Promise<UpsertCartResult> {
    try {
        return await upsertCartThroughApi(current);
    } catch (error) {
        if (!(error instanceof CartAlreadyConvertedError) && !(error instanceof CartNotOwnedError)) throw error;
        const fresh = CartStorageService.createLocalGuestCart(
            current.restaurantId,
            current.tenantId,
            current.orderType,
            customerId,
            isAnonymous,
            current.tableGuid,
        );
        const moved = Cart.fromJSON({
            ...current.toJSON(),
            cartId: fresh.cartId,
            guid: fresh.guid,
            customerId: customerId ?? current.customerId,
            createdAt: fresh.createdAt,
            updatedAt: new Date(),
            expiresAt: fresh.expiresAt,
            convertedAt: undefined,
            orderGuid: undefined,
            lastCheckoutFailure: undefined,
            checkoutFailureCount: undefined,
            syncedAt: undefined,
        });
        logger.warn('Cart refused for good — moving items to a fresh cart', {
            reason: error instanceof CartNotOwnedError ? 'not-owned' : 'converted',
            fromCartGuid: current.guid,
            items: moved.items.length,
        });
        const result = await upsertCartThroughApi(moved);
        return { ...result, rehomed: true };
    }
}

/** The caller's live cart through the API (`GET /carts/mine`). */
async function loadMyCart(tenantId: number, locationNumericId: number): Promise<Cart | null> {
    return cartsApi.getMyCart(tenantId, locationNumericId);
}

/**
 * Query keys for cart-related data
 */
export const cartKeys = {
    all: ['carts'] as const,
    byId: (cartId: string) => [...cartKeys.all, 'id', cartId] as const,
    byRestaurant: (locationNumericId: number, tenantId: number) => [...cartKeys.all, 'restaurant', { restaurantId: locationNumericId, tenantId }] as const,
    byCustomer: (customerId: string, locationNumericId: number, tenantId: number) => [...cartKeys.byRestaurant(locationNumericId, tenantId), 'customer', customerId] as const,
};

export interface UseCartOptions {
    restaurantId: number;
    tenantId: number;
    /** Firestore document ID for tenant */
    tenantDocId: string;
    /** Firestore document ID for restaurant */
    restaurantDocId: string;
    orderType?: CartOrderType;
    /**
     * `dineIn` only — the table the guest scanned. Passed straight to cart
     * CREATION: a table cart that forgets its table cannot check out, because
     * the server refuses a dine-in order that names no table.
     */
    tableGuid?: string;
    customerId?: string;
    taxRates?: ITaxRate[];
}

export interface UseCartReturn {
    // Cart state
    cart: Cart | null;
    isLoading: boolean;
    isSyncing: boolean;

    // Cart info
    itemCount: number;
    uniqueItemCount: number;
    subtotal: number;
    tax: number;
    total: number;
    isEmpty: boolean;
    isValid: boolean;
    isExpired: boolean;
    isGuest: boolean;

    // Cart operations
    addItem: (itemData: ICartItemData) => Promise<void>;
    updateItemQuantity: (cartItemId: string, quantity: number) => Promise<void>;
    removeItem: (cartItemId: string) => Promise<void>;
    updateItemNotes: (cartItemId: string, notes: string) => Promise<void>;
    replaceItem: (cartItemId: string, updatedItemData: ICartItemData) => Promise<void>;
    clearCart: () => Promise<void>;
    updateOrderConfig: (orderConfig: {
        orderType?: CartOrderType;
        scheduledTime?: Date;
        deliveryAddress?: string;
        deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    }) => Promise<void>;
    /**
     * Stamp the guest code (printed QR) the guest landed with. Fire-and-forget:
     * applied to the cache + localStorage at once, synced in the background.
     * The server re-validates it at checkout — see cartService.setGuestCodeGuid.
     */
    setGuestCodeGuid: (guestCodeGuid: string) => void;

    // Utility functions
    findItem: (cartItemId: string) => CartItem | undefined;
    getMenuItemQuantity: (menuItemId: number) => number;
    getMenuItemQuantityByIdentifier: (menuItemId?: number, menuItemGuid?: string) => number;
    validateForCheckout: () => { isValid: boolean; errors: string[] };

    // Cart management
    refreshCart: () => void;
    /**
     * Persist the cart and resolve to the cart that was ACTUALLY persisted —
     * a fresh one when the cached cart had already been placed as an order.
     * Hand THAT cart's guid to checkout.
     */
    syncToFirestore: () => Promise<Cart>;
    deleteCart: () => Promise<void>;
}

/**
 * Hook for managing shopping cart state and operations
 * Refactored to use TanStack Query for proper state management
 */
export function useCart(options: UseCartOptions): UseCartReturn {
    const { restaurantId: locationNumericId, tenantId, tenantDocId, restaurantDocId: locationDocId, orderType = 'pickup', tableGuid, customerId, taxRates = [] } = options;

    const queryClient = useQueryClient();

    // Get current auth state
    const { data: authData, isLoading: isAuthLoading } = useAuth();
    const { authUser } = authData || {};
    const currentCustomerId = customerId || authUser?.uid;
    const isAuthenticated = !!currentCustomerId;

    // Query key for this cart - use consistent key based on restaurant/tenant only
    // This prevents duplicate queries when auth state changes
    const cartQueryKey = cartKeys.byRestaurant(locationNumericId, tenantId);

    // Main cart query - loads from Firestore or localStorage
    const { data: cart, isLoading, refetch } = useQuery({
        queryKey: cartQueryKey,
        queryFn: async (): Promise<Cart> => {
            logger.debug('Loading cart', { restaurantId: locationNumericId, tenantId, currentCustomerId, isAuthenticated });

            let loadedCart: Cart | null = null;
            let firestoreQuerySucceeded = false;

            // Strategy 1: If authenticated, Firestore is the source of truth
            if (isAuthenticated && currentCustomerId) {
                logger.debug('Authenticated user - Firestore is source of truth');
                try {
                    const firestoreCart = await loadMyCart(
                        tenantId,
                        locationNumericId
                    );
                    firestoreQuerySucceeded = true; // Query succeeded, even if result is null

                    if (firestoreCart) {
                        loadedCart = firestoreCart;
                        logger.debug('Loaded cart from Firestore');
                        // Update localStorage cache to match Firestore
                        CartStorageService.saveCart(firestoreCart);
                    } else {
                        // Firestore has no cart for THIS customer. Before discarding the
                        // local cart, adopt an in-progress *guest* cart left behind when an
                        // anonymous session became authenticated (phone sign-in). The
                        // anonymous→linked upgrade preserves the UID so there's no mismatch;
                        // this covers the returning-customer case where signing into an
                        // existing account changes the UID. We only adopt a guest cart with
                        // items for the same restaurant — a real (non-guest) cart is never
                        // adopted, so one customer's cart can't leak to another on a shared
                        // browser.
                        const localCart = CartStorageService.loadCart();
                        const adoptable =
                            !!localCart &&
                            localCart.restaurantId === locationNumericId &&
                            localCart.tenantId === tenantId &&
                            localCart.items.length > 0 &&
                            (localCart.isGuest ?? true) &&
                            localCart.customerId !== currentCustomerId;

                        if (adoptable && localCart) {
                            logger.info('Adopting in-progress guest cart for authenticated customer', {
                                fromCustomerId: localCart.customerId,
                                toCustomerId: currentCustomerId,
                                items: localCart.items.length,
                            });
                            const adopted = Cart.fromJSON({
                                ...localCart.toJSON(),
                                customerId: currentCustomerId,
                                isGuest: authUser?.isAnonymous ?? false,
                            });
                            CartStorageService.saveCart(adopted);
                            loadedCart = adopted;
                            try {
                                // The adopted guid's document belongs to the old
                                // (anonymous) uid, so the server refuses it and the
                                // items move to a fresh cart owned by this uid.
                                const persisted = await persistCartWithRehome(adopted, currentCustomerId, authUser?.isAnonymous);
                                loadedCart = persisted.cart;
                                CartStorageService.saveCart(persisted.cart);
                            } catch (error) {
                                logger.warn('Failed to persist adopted cart', { error });
                            }
                        } else {
                            // No adoptable cart - clear stale localStorage
                            logger.debug('No cart in Firestore - clearing stale localStorage');
                            CartStorageService.clearCart();
                        }
                    }
                } catch (error) {
                    logger.warn('Firestore query failed (network error), using localStorage fallback', { error: error instanceof Error ? error.message : String(error) });
                    firestoreQuerySucceeded = false; // Query failed, can use localStorage
                }
            }

            // Strategy 2: Fallback to localStorage ONLY if Firestore query failed (network error)
            // If Firestore query succeeded but returned null, we respect that (cart was deleted)
            if (!loadedCart && !firestoreQuerySucceeded) {
                logger.debug('Using localStorage fallback (offline or network error)');
                const localCart = CartStorageService.loadCart();

                // Check if local cart matches current context
                if (localCart &&
                    (localCart.restaurantId !== locationNumericId || localCart.tenantId !== tenantId)) {
                    logger.warn('Restaurant/tenant mismatch - clearing cart');
                    CartStorageService.clearCart();
                } else if (localCart) {
                    // Check if cart's customerId matches current user
                    if (isAuthenticated && currentCustomerId && localCart.customerId && localCart.customerId !== currentCustomerId) {
                        logger.warn('Customer ID mismatch - clearing cart');
                        CartStorageService.clearCart();
                    } else {
                        loadedCart = localCart;
                        logger.debug('Using localStorage cart (offline mode)', { itemCount: localCart.items.length });
                    }
                }
            }

            // Strategy 3: Create new cart if none exists
            if (!loadedCart) {
                if (isAuthenticated && currentCustomerId) {
                    // Authenticated (incl. anonymous-auth). Created LOCALLY: the
                    // server allocates the real numeric cartId on the first sync
                    // (PUT /carts/:guid), so the browser never touches the
                    // counters collection — that read-then-write raced, and it
                    // was another App Check-gated SDK call.
                    logger.debug('Creating new cart (authenticated)');
                    loadedCart = CartStorageService.createLocalGuestCart(
                        locationNumericId,
                        tenantId,
                        orderType,
                        currentCustomerId,
                        authUser?.isAnonymous,
                        tableGuid,
                    );
                } else {
                    // No uid yet (anon auth still resolving, or blocked/slow in this
                    // browser). Create a guest cart SYNCHRONOUSLY with no Firestore
                    // round-trip so the customer can add items immediately. The
                    // customerId is backfilled (and the cart becomes syncable) once
                    // auth lands — see the effect below.
                    logger.debug('Creating new cart (guest, no uid yet)');
                    loadedCart = CartStorageService.createLocalGuestCart(
                        locationNumericId,
                        tenantId,
                        orderType,
                        undefined,
                        undefined,
                        tableGuid,
                    );
                }
                logger.debug('Created new cart', { cartId: loadedCart.cartId, isGuest: loadedCart.isGuest });

                // Save to localStorage cache
                CartStorageService.saveCart(loadedCart);

                // Don't save empty unconfigured cart to Firestore yet (waste of resources)
                // It will auto-sync when configured or when first item is added
            }

            return loadedCart;
        },
        // Enable as soon as the restaurant/tenant context is known. We deliberately
        // do NOT wait for `currentCustomerId` (the anonymous-auth uid): a guest must
        // be able to load/create a cart and add items even before anon auth resolves,
        // or when it's slow/blocked (mobile in-app browsers). The queryFn creates a
        // local guest cart in that window and reconciles the uid when auth lands.
        enabled: !!locationNumericId && !!tenantId && !!tenantDocId && !!locationDocId && !isAuthLoading,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes (renamed from cacheTime)
        // The cart is SESSION state (mutations write through to localStorage +
        // Firestore). Don't reload it from Firestore on tab focus/reconnect — a
        // background refetch added lag AND could clobber the just-set order
        // config (orderType/configuredAt) with a slower-to-propagate server copy,
        // which made the app re-prompt for pickup/delivery. The choice now
        // persists for the whole session and only resets when the cart is cleared
        // after an order is placed.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // Also don't silently reload from Firestore when a later component mounts
        // (cart modal, header, etc.). The first load fetches; after that the cart
        // lives in the cache for the session so the just-set order config
        // (orderType / configuredAt / scheduledTime / address) is never clobbered
        // by a slower-to-propagate server copy mid-session.
        refetchOnMount: false,
    });

    // Backfill the auth uid onto a guest cart that was created before auth
    // resolved (the local-guest path above leaves customerId undefined). Once the
    // anonymous/real uid lands, stamp it so the cart can persist to Firestore —
    // the carts security rule keys reads on `customerId == request.auth.uid`, so a
    // customerId-less cart can never sync. Runs once per uid change; the identity
    // guard makes repeat renders a no-op.
    useEffect(() => {
        if (!currentCustomerId) return;
        const current = queryClient.getQueryData<Cart>(cartQueryKey);
        if (!current || current.customerId === currentCustomerId) return;
        const stamped = Cart.fromJSON({
            ...current.toJSON(),
            customerId: currentCustomerId,
            isGuest: authUser?.isAnonymous ?? current.isGuest ?? true,
        });
        CartStorageService.saveCart(stamped);
        queryClient.setQueryData(cartQueryKey, stamped);
    }, [currentCustomerId, cartQueryKey, queryClient, authUser?.isAnonymous]);

    // Checkout lives on another origin and places the order there, so a guest
    // who pays and then presses Back lands on this page restored from the
    // back/forward cache — JS state frozen, cart cache still holding the cart
    // that just became an order. Reload it: loadCartFromFirestore skips a
    // converted cart, so the guest gets a fresh one instead of their last
    // order's items and a checkout that answers "Cart Not Found".
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                void queryClient.invalidateQueries({ queryKey: cartKeys.byRestaurant(locationNumericId, tenantId) });
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [locationNumericId, tenantId, queryClient]);

    // The ONE write path from this hook — through the API (see
    // upsertCartThroughApi). Returns the cart that was actually persisted,
    // which is NOT always the one passed in:
    //  - the server allocates the numeric cartId, so a new cart comes back with
    //    the server's id in place of the local placeholder;
    //  - when the server says this cart already became an order (a tab left
    //    open across a checkout, a back-button bfcache restore), the items move
    //    onto a FRESH cart (new cartId + guid) and that one becomes the live
    //    cart. Without this the guest's checkout handed over the placed cart's
    //    guid and got "Cart Not Found" on every press.
    const persistCart = async (current: Cart): Promise<Cart> => {
        const result = await persistCartWithRehome(current, currentCustomerId, authUser?.isAnonymous);
        if (result.rehomed || result.cart.guid !== current.guid) {
            logger.warn('Cart re-homed by the server — the fresh cart is now live', {
                fromCartId: current.cartId,
                toCartId: result.cart.cartId,
                orderGuid: result.convertedOrderGuid,
                items: result.cart.items.length,
            });
            CartStorageService.saveCart(result.cart);
            queryClient.setQueryData(cartQueryKey, result.cart);
            return result.cart;
        }
        // Same cart. Adopt the server's id onto the LATEST cached cart (an
        // optimistic mutation may have moved it on since this write started).
        const latest = queryClient.getQueryData<Cart>(cartQueryKey);
        if (latest && latest.guid === result.cart.guid && latest.cartId !== result.cart.cartId) {
            const withId = Cart.fromJSON({ ...latest.toJSON(), cartId: result.cart.cartId });
            CartStorageService.saveCart(withId);
            queryClient.setQueryData(cartQueryKey, withId);
        }
        return current.cartId === result.cart.cartId
            ? current
            : Cart.fromJSON({ ...current.toJSON(), cartId: result.cart.cartId });
    };

    // Persist the already-applied cart (read from cache) to Firestore in the
    // background. Used by the optimistic cart mutations below — the UI is updated
    // synchronously in onMutate, so this network write is never in the critical
    // path. Failure is non-fatal (the cart still lives in localStorage + cache).
    const syncCartToFirestore = async () => {
        const current = queryClient.getQueryData<Cart>(cartQueryKey);
        if (current && isAuthenticated && currentCustomerId) {
            try {
                await persistCart(current);
            } catch (error) {
                logger.error('Failed to auto-sync cart to Firestore', error);
            }
        }
    };

    // Mutation: Add item to cart — OPTIMISTIC (instant). onMutate validates +
    // applies the change to localStorage + the query cache synchronously, so the
    // cart badge updates the moment the customer taps "+"; Firestore syncs after.
    const addItemMutation = useMutation({
        onMutate: async (itemData: ICartItemData) => {
            let freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) {
                // The tap landed before the cart query produced a cart (very fast
                // tap, or anon auth still resolving). Create a local guest cart
                // synchronously so the add NEVER silently fails.
                freshCart = CartStorageService.createLocalGuestCart(
                    locationNumericId,
                    tenantId,
                    orderType,
                    currentCustomerId,
                    isAuthenticated ? authUser?.isAnonymous : true,
                    tableGuid,
                );
                CartStorageService.saveCart(freshCart);
                queryClient.setQueryData(cartQueryKey, freshCart);
            }
            const validation = cartService.canAddItemToCart(freshCart, itemData);
            if (!validation.canAdd) {
                throw new Error(validation.reason || 'Cannot add item to cart');
            }
            const updatedCart = cartService.addItemToCart(freshCart, itemData);
            CartStorageService.saveCart(updatedCart);
            queryClient.setQueryData(cartQueryKey, updatedCart);
        },
        mutationFn: syncCartToFirestore,
        onError: (error) => {
            logger.error('Failed to add item to cart', error);
        },
    });

    // Mutation: Update item quantity — OPTIMISTIC.
    const updateQuantityMutation = useMutation({
        onMutate: async ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => {
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');
            const updatedCart = cartService.updateItemQuantity(freshCart, cartItemId, quantity);
            CartStorageService.saveCart(updatedCart);
            queryClient.setQueryData(cartQueryKey, updatedCart);
        },
        mutationFn: syncCartToFirestore,
        onError: (error) => {
            logger.error('Failed to update item quantity', error);
        },
    });

    // Mutation: Remove item — OPTIMISTIC.
    const removeItemMutation = useMutation({
        onMutate: async (cartItemId: string) => {
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');
            const updatedCart = cartService.removeItemFromCart(freshCart, cartItemId);
            CartStorageService.saveCart(updatedCart);
            queryClient.setQueryData(cartQueryKey, updatedCart);
        },
        mutationFn: syncCartToFirestore,
        onError: (error) => {
            logger.error('Failed to remove item from cart', error);
        },
    });

    // Mutation: Update item notes
    const updateNotesMutation = useMutation({
        mutationFn: async ({ cartItemId, notes }: { cartItemId: string; notes: string }) => {
            // Get fresh cart from cache to avoid stale state issues
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');

            const updatedCart = cartService.updateItemNotes(freshCart, cartItemId, notes);

            // Save to localStorage immediately
            CartStorageService.saveCart(updatedCart);

            // Auto-sync to Firestore for consistency
            if (isAuthenticated && currentCustomerId) {
                try {
                    return await persistCart(updatedCart);
                } catch (error) {
                    console.error('[useCart] Failed to auto-sync cart:', error);
                }
            }

            return updatedCart;
        },
        onSuccess: (updatedCart) => {
            queryClient.setQueryData(cartQueryKey, updatedCart);
        }
    });

    // Mutation: Replace cart item (for editing - more efficient than remove + add)
    const replaceItemMutation = useMutation({
        mutationFn: async ({ cartItemId, updatedItemData }: { cartItemId: string; updatedItemData: ICartItemData }) => {
            // Get fresh cart from cache to avoid stale state issues
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');

            const updatedCart = cartService.replaceCartItem(freshCart, cartItemId, updatedItemData);

            // Save to localStorage immediately
            CartStorageService.saveCart(updatedCart);

            // Auto-sync to Firestore for consistency
            if (isAuthenticated && currentCustomerId) {
                try {
                    return await persistCart(updatedCart);
                } catch (error) {
                    console.error('[useCart] Failed to auto-sync cart:', error);
                }
            }

            return updatedCart;
        },
        onSuccess: (updatedCart) => {
            queryClient.setQueryData(cartQueryKey, updatedCart);
        }
    });

    // Mutation: Clear cart
    const clearCartMutation = useMutation({
        mutationFn: async () => {
            // Get fresh cart from cache to avoid stale state issues
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');

            const clearedCart = cartService.clearCart(freshCart);

            // Save to localStorage immediately
            CartStorageService.saveCart(clearedCart);

            // Auto-sync to Firestore for consistency
            if (isAuthenticated && currentCustomerId) {
                try {
                    return await persistCart(clearedCart);
                } catch (error) {
                    console.error('[useCart] Failed to auto-sync cart:', error);
                }
            }

            return clearedCart;
        },
        onSuccess: (clearedCart) => {
            queryClient.setQueryData(cartQueryKey, clearedCart);
        }
    });

    // Mutation: Delete cart (permanent deletion after successful payment)
    const deleteCartMutation = useMutation({
        mutationFn: async () => {
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) throw new Error('No cart available');

            logger.debug('Deleting cart', { cartId: freshCart.cartId });

            // Delete through the API (owner-only, never a placed cart).
            await cartsApi.deleteCart(freshCart.guid, tenantId, locationNumericId);

            // Clear from local storage
            CartStorageService.clearCart();

            logger.info('Cart deleted successfully');
        },
        onSuccess: () => {
            // Invalidate cart query to force refresh (will create new cart on next load)
            queryClient.invalidateQueries({ queryKey: cartQueryKey });
        },
        onError: (error) => {
            logger.error('Failed to delete cart', error);
        }
    });

    const updateOrderConfigMutation = useMutation({
        // Apply the config change OPTIMISTICALLY so the pickup/delivery toggle
        // (and time/address) flips instantly — no waiting on a Firestore round
        // trip. The cache + localStorage update synchronously here; Firestore is
        // synced in the background in mutationFn.
        onMutate: async (orderConfig: {
            orderType?: CartOrderType;
            scheduledTime?: Date;
            deliveryAddress?: string;
            deliveryAddressComponents?: IOrderDeliveryAddressComponents;
        }) => {
            await queryClient.cancelQueries({ queryKey: cartQueryKey });
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart) return;
            const updatedCart = cartService.updateOrderConfig(freshCart, orderConfig);
            CartStorageService.saveCart(updatedCart);
            queryClient.setQueryData(cartQueryKey, updatedCart);
        },
        mutationFn: async () => {
            // Durably persist the already-applied config (read from cache) to
            // Firestore. Failure is non-fatal — the cart still lives in
            // localStorage + the query cache for the session.
            const current = queryClient.getQueryData<Cart>(cartQueryKey);
            if (current && isAuthenticated && currentCustomerId) {
                try {
                    await persistCart(current);
                } catch (error) {
                    console.error('[useCart] Failed to auto-sync cart config:', error);
                }
            }
        },
    });

    // Mutation: stamp the landing guest code — OPTIMISTIC, same shape as
    // updateOrderConfigMutation. The Firestore write is skipped for an EMPTY,
    // UNCONFIGURED cart, keeping the rule the cart query follows at creation
    // ("don't save an empty unconfigured cart"): the stamp lives in the cache,
    // and the first item add or order config syncs the whole cart — guid
    // included — and checkout always syncs before handing off.
    const setGuestCodeGuidMutation = useMutation({
        onMutate: async (guestCodeGuid: string) => {
            await queryClient.cancelQueries({ queryKey: cartQueryKey });
            const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;
            if (!freshCart || freshCart.guestCodeGuid === guestCodeGuid) return;
            const updatedCart = cartService.setGuestCodeGuid(freshCart, guestCodeGuid);
            CartStorageService.saveCart(updatedCart);
            queryClient.setQueryData(cartQueryKey, updatedCart);
        },
        mutationFn: async () => {
            const current = queryClient.getQueryData<Cart>(cartQueryKey);
            if (!current || (current.isEmpty && !current.isConfigured)) return;
            await syncCartToFirestore();
        },
        onError: (error) => {
            logger.error('Failed to stamp guest code on cart', error);
        },
    });

    // Cart operations using mutations
    const addItem = useCallback(async (itemData: ICartItemData) => {
        await addItemMutation.mutateAsync(itemData);
    }, [addItemMutation]);

    const updateItemQuantity = useCallback(async (cartItemId: string, quantity: number) => {
        await updateQuantityMutation.mutateAsync({ cartItemId, quantity });
    }, [updateQuantityMutation]);

    const removeItem = useCallback(async (cartItemId: string) => {
        await removeItemMutation.mutateAsync(cartItemId);
    }, [removeItemMutation]);

    const updateItemNotes = useCallback(async (cartItemId: string, notes: string) => {
        await updateNotesMutation.mutateAsync({ cartItemId, notes });
    }, [updateNotesMutation]);

    const replaceItem = useCallback(async (cartItemId: string, updatedItemData: ICartItemData) => {
        await replaceItemMutation.mutateAsync({ cartItemId, updatedItemData });
    }, [replaceItemMutation]);

    const clearCart = useCallback(async () => {
        await clearCartMutation.mutateAsync();
    }, [clearCartMutation]);

    const updateOrderConfig = useCallback(async (orderConfig: {
        // Matches `UseCartReturn.updateOrderConfig`. `dineIn` is accepted for
        // shape parity only — a table cart is created dine-in and nothing
        // offers a guest at a table a way to change it (the pickup/delivery
        // affordances are exactly what table mode turns off).
        orderType?: CartOrderType;
        scheduledTime?: Date;
        deliveryAddress?: string;
        deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    }) => {
        await updateOrderConfigMutation.mutateAsync(orderConfig);
    }, [updateOrderConfigMutation]);

    const setGuestCodeGuid = useCallback((guestCodeGuid: string) => {
        setGuestCodeGuidMutation.mutate(guestCodeGuid);
    }, [setGuestCodeGuidMutation]);

    const deleteCart = useCallback(async () => {
        await deleteCartMutation.mutateAsync();
    }, [deleteCartMutation]);

    // Utility functions
    const findItem = useCallback((cartItemId: string): CartItem | undefined => {
        return cart?.findItem(cartItemId);
    }, [cart]);

    const getMenuItemQuantity = useCallback((menuItemId: number): number => {
        if (!cart) return 0;
        return cartService.getMenuItemQuantityInCart(cart, menuItemId);
    }, [cart]);

    const getMenuItemQuantityByIdentifier = useCallback((menuItemId?: number, menuItemGuid?: string): number => {
        if (!cart) return 0;
        return cartService.getMenuItemQuantityByIdentifier(cart, menuItemId, menuItemGuid);
    }, [cart]);

    const validateForCheckout = useCallback(() => {
        if (!cart) return { isValid: false, errors: ['No cart available'] };
        return cartService.validateCartForCheckout(cart);
    }, [cart]);

    const refreshCart = useCallback(() => {
        refetch();
    }, [refetch]);

    const syncToFirestore = useCallback(async () => {
        // Get the freshest cart from React Query cache (in case it was just updated)
        const freshCart = queryClient.getQueryData<Cart>(cartQueryKey) || cart;

        if (!freshCart || !isAuthenticated || !currentCustomerId) {
            throw new Error('Cannot sync to Firestore: cart not available or user not authenticated');
        }
        logger.debug('Syncing local cart to Firestore', {
            cartId: freshCart.cartId,
            itemCount: freshCart.itemCount,
            orderType: freshCart.orderType,
            scheduledTime: freshCart.scheduledTime,
            deliveryAddress: freshCart.deliveryAddress
        });
        // Use upsert to handle case where local cart exists but Firestore cart was deleted.
        // The persisted cart may be a FRESH one (see persistCart) — callers that
        // hand a guid to checkout must use the returned cart, never their render's.
        const persisted = await persistCart(freshCart);
        logger.info('Cart successfully synced to Firestore');
        return persisted;
    }, [cart, cartQueryKey, queryClient, isAuthenticated, currentCustomerId, tenantId, locationNumericId]);

    // Calculate cart summary
    const cartSummary = cart ? cartService.getCartSummary(cart, taxRates) : {
        itemCount: 0,
        uniqueItemCount: 0,
        subtotal: 0,
        tax: 0,
        total: 0,
        isEmpty: true,
        isValid: false,
        isExpired: false
    };

    const isSyncing = addItemMutation.isPending || updateQuantityMutation.isPending ||
        removeItemMutation.isPending || updateNotesMutation.isPending ||
        replaceItemMutation.isPending || clearCartMutation.isPending || updateOrderConfigMutation.isPending ||
        setGuestCodeGuidMutation.isPending || deleteCartMutation.isPending;

    return {
        // Cart state
        cart: cart || null,
        isLoading,
        isSyncing,

        // Cart info (from summary)
        itemCount: cartSummary.itemCount,
        uniqueItemCount: cartSummary.uniqueItemCount,
        subtotal: cartSummary.subtotal,
        tax: cartSummary.tax,
        total: cartSummary.total,
        isEmpty: cartSummary.isEmpty,
        isValid: cartSummary.isValid,
        isExpired: cartSummary.isExpired,
        isGuest: cart?.isAnonymous ?? true,

        // Cart operations
        addItem,
        updateItemQuantity,
        removeItem,
        updateItemNotes,
        replaceItem,
        clearCart,
        updateOrderConfig,
        setGuestCodeGuid,

        // Utility functions
        findItem,
        getMenuItemQuantity,
        getMenuItemQuantityByIdentifier,
        validateForCheckout,

        // Cart management
        refreshCart,
        syncToFirestore,
        deleteCart,
    };
}

/**
 * Hook to load a specific cart by GUID from Firestore
 * Used by the checkout app to load cart from URL parameter
 *
 * The GUID alone resolves the cart, so the read starts on page load — it never
 * waits for the location to resolve from the slug.
 *
 * @param cartGuid - The cart GUID (UUID) to load
 * @returns Query result with cart data
 */
export function useGetCartById(cartGuid: string | null) {
    return useQuery({
        queryKey: cartGuid ? cartKeys.byId(cartGuid) : ['carts', 'null'],
        queryFn: async (): Promise<Cart | null> => {
            if (!cartGuid) {
                logger.debug('No cartGuid provided');
                return null;
            }

            logger.debug('Loading cart by GUID', { cartGuid });

            const cart = await cartService.loadCartById(cartGuid);

            if (!cart) {
                logger.warn('Cart not found or expired', { cartGuid });
                return null;
            }

            logger.info('Successfully loaded cart', {
                cartId: cart.cartId,
                guid: cart.guid,
                itemCount: cart.items.length,
                total: cartService.calculateSubtotal(cart)
            });

            return cart;
        },
        enabled: !!cartGuid,
        staleTime: 1000 * 60 * 2, // 2 minutes - shorter for checkout
        gcTime: 1000 * 60 * 10, // 10 minutes
        retry: 2,
        // The checkout page is a cross-app hand-off target: the customer can hop
        // back to the ordering app, change the order config (time / order type /
        // items), and return via back-forward navigation, which restores this
        // page from bfcache without re-running the query. Always refetch on
        // mount and focus so a config change made in the ordering app is never
        // masked by a ≤2-min-old cached cart (it's a single-doc read).
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
    });
}

/**
 * Order-config mutation for a HANDED-OFF cart (checkout app parity controls).
 *
 * The ordering app mutates its cart through `useCart` (localStorage-backed,
 * customer-scoped). The checkout app instead loads the cart by GUID
 * (`useGetCartById`) — this hook gives it the SAME config powers (order type,
 * scheduled time, delivery address) against that query's cache:
 * apply via cartService, persist to Firestore durably, then update the
 * `cartKeys.byId` cache and invalidate the order preview (order type / time
 * changes shift the server-derived totals: delivery fee, Drive ETA).
 *
 * `scheduledTime: undefined` means ASAP — the Firestore sync DELETES the old
 * slot (see resolveClearedCartFields), which is what lets a customer recover
 * from a passed scheduled time without leaving checkout.
 */
export function useUpdateCartOrderConfig() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: {
            cart: Cart;
            tenantId: number;
            restaurantId: number;
            config: {
                orderType?: CartOrderType;
                scheduledTime?: Date;
                deliveryAddress?: string;
                deliveryAddressComponents?: IOrderDeliveryAddressComponents;
            };
        }): Promise<Cart> => {
            const updated = cartService.updateOrderConfig(input.cart, input.config);
            // Through the API, update-only: a missing cart is a 404 and a
            // placed one a 410 — never the silent zero-match "success" the
            // SDK's updateByQuery gave.
            await upsertCartThroughApi(updated, { mustExist: true });
            return updated;
        },
        onSuccess: (updated) => {
            queryClient.setQueryData(cartKeys.byId(updated.guid), updated);
            queryClient.invalidateQueries({ queryKey: ['orderPreview'] });
        },
    });
}

/**
 * Accept the SERVER's scheduled-menu-pricing refusal on a handed-off cart
 * (checkout app): each `scheduled_price_changed` line becomes the server's
 * `repricedLine`, each `not_available_now` line is removed — the guest has
 * just seen and confirmed exactly that. Persisted, then the preview is asked
 * again. The server's answer is authoritative, so this cannot loop: the cart
 * now holds what the server prices at that instant.
 */
export function useApplyServerReprice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: {
            cart: Cart;
            tenantId: number;
            restaurantId: number;
            refusals: readonly CartLineRefusal[];
        }): Promise<Cart> => {
            const updated = applyServerRepricing(input.cart, input.refusals);
            await upsertCartThroughApi(updated, { mustExist: true });
            return updated;
        },
        onSuccess: (updated) => {
            queryClient.setQueryData(cartKeys.byId(updated.guid), updated);
            queryClient.invalidateQueries({ queryKey: ['orderPreview'] });
        },
    });
}
